import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import {
  JourneyForm,
  PageBody,
  Spinner,
  backLinkVariants,
  type JourneySubmitValue,
} from '@wundo/ui';
import { JOURNEY_LIMITS } from '@wundo/shared/journeyLimits.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import { JourneysFeatureNotice } from '@/components/journeys/JourneysFeatureNotice.tsx';
import { JOURNEY_FORM_LABELS } from '@/components/journeys/journeyLabels.ts';
import {
  journeyRefusal,
  type JourneyRefusal,
} from '@/components/journeys/journeyRefusal.ts';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { createJourney, listJourneyTypes } from '@/lib/journeys.ts';

export const Route = createFileRoute('/_app/journeys/new')({
  staticData: { orgScope: 'required' },
  component: NewJourneyPage,
});

const NO_REFUSAL: JourneyRefusal = { errors: {} };

/** A new journey in the active organisation: one employee, one manager, one type, from their first day. */
export function NewJourneyPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id ?? null;

  const types = useQuery({
    queryKey: ['journeyTypes', organisationId],
    queryFn: () => listJourneyTypes(organisationId!),
    enabled: organisationId !== null,
  });

  const [manager, setManager] = useState<UserOption | null>(null);
  /* One object per refusal: the form treats a new object as a new refusal. */
  const [refusal, setRefusal] = useState<JourneyRefusal>(NO_REFUSAL);

  const create = useMutation({
    mutationFn: (value: JourneySubmitValue) => createJourney(organisationId!, value),
    onMutate: () => setRefusal(NO_REFUSAL),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['journeys', organisationId] });
      await router.navigate({
        to: '/journeys/organisations/$organisationId/$id',
        params: { organisationId: organisationId!, id: created.id },
      });
    },
    onError: (failure) => setRefusal(journeyRefusal(failure)),
  });

  function renderContent() {
    if (types.isError) {
      return (
        <p role='alert' className='text-sm text-danger'>
          Failed to load the journey types
        </p>
      );
    }
    if (organisationId === null || types.data === undefined) {
      return <Spinner />;
    }
    return (
      <>
        {types.data.length === 0 && (
          <p className='text-sm text-ink-muted'>
            {organisation?.name ?? 'This organisation'} has no journey types yet.{' '}
            <Link to='/journey-types/new' className='font-semibold underline'>
              Create one first
            </Link>
          </p>
        )}
        <JourneyForm
          typeOptions={types.data.map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          managerId={manager?.id ?? null}
          renderManager={({ id, invalid, 'aria-describedby': describedBy }) => (
            <UserPicker
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              organisationId={organisationId}
              placeholder='Search a manager...'
              clearable
              value={manager}
              onChange={setManager}
            />
          )}
          renderRunningJourneyLink={(journeyId) => (
            <Link
              to='/journeys/organisations/$organisationId/$id'
              params={{ organisationId, id: journeyId }}
              className='font-semibold underline'
            >
              Open the running journey
            </Link>
          )}
          onSubmit={(value) => create.mutate(value)}
          onCancel={() => void router.navigate({ to: '/journeys' })}
          submitting={create.isPending}
          errors={refusal.errors}
          formError={refusal.formError}
          limits={JOURNEY_LIMITS}
          labels={JOURNEY_FORM_LABELS}
        />
      </>
    );
  }

  return (
    <>
      <Link to='/journeys' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Journeys
      </Link>
      <PageHeader title='New journey' />
      <PageBody flush className='mt-7'>
        <ReadingColumn className='flex flex-col gap-5'>
          {organisationId !== null && (
            <JourneysFeatureNotice organisationId={organisationId} />
          )}

          {renderContent()}
        </ReadingColumn>
      </PageBody>
    </>
  );
}
